// INV-LEVEL: two levels. Block-level containers (the document body, a quote, callout, column, list item, toggle) hold
// blocks; block content arriving at a text row there lands in the row's own block list (placement.ts). Everything else
// holds inline content only: a text row's own content (a paragraph, heading, code block, table cell), and the fields with
// no block-level position (a question's prompt, options and explanation, a caption, a quote's author). Block content
// arriving in an inline-only slot is flattened to its inline content, or the command is refused.
import { cellInline, isCellInline } from '@w/content-blocks/src/cell-inline'
import type { Block, Blocks } from '@w/content-blocks/src/block'
import { children, isRow } from './tree-read'

/** Inline content: an inline block (the codec's cell inline set, and a pages group: page cards in the text) whose own
 * content is inline. */
export function isInlineContent(block: Block): boolean {
  return (isCellInline(block) || block.type === 'pages') && children(block).every(child => isInlineContent(child))
}

/** Whether block content fits an inline-only slot as it is. */
export function isInline(blocks: Blocks): boolean {
  return blocks.every(block => isInlineContent(block))
}

/**
 * The inline content of a fragment, for an inline-only slot: the codec's cell projection (`cellInline`: each block's
 * runs, blocks separated by line breaks, a question's prompt and options, media as a link to its source). A question
 * field (`join: false`) joins its blocks' runs directly (Lexical CASE 3a): a container's blocks are opened, a divider
 * has no runs, and each other block keeps its own projection (a code block its lines).
 */
export function inlineContent(fragment: Blocks, join = true): Blocks {
  if (isInline(fragment)) return fragment
  if (join) return cellInline(fragment, () => '')
  return fragment.flatMap((block): Blocks => isInlineContent(block) ? [block] : block.type === 'hr' ? []
    : block.type !== 'table' && !isRow(block) && 'children' in block && Array.isArray(block.children)
      ? inlineContent(block.children, false) : cellInline([block], () => ''))
}

/** Whether a text row is an inline-only slot: a table cell, or any row of a field scope (prompt, option, explanation). */
export function inInlineSlot(state: { scope?: unknown }, row: { block: Block }): boolean {
  return state.scope !== undefined || row.block.type === 'td'
}

/**
 * A fragment's text rows split at every block they hold inline (Markdown `One ![](a.png) Two` reads as a paragraph
 * holding an image): the block stands between the row's halves, so a paste places it as a block.
 */
export function splitAtBlocks(fragment: Blocks): Blocks {
  return fragment.flatMap((block): Blocks => {
    if (isRow(block) && block.type !== 'td' && !isInline(children(block))) {
      const parts: Blocks = []
      let run: Blocks = []
      const flush = () => { if (run.length > 0) parts.push({ ...block, id: '', children: run } as Block); run = [] }
      for (const child of children(block)) {
        if (isInlineContent(child)) run.push(child)
        else { flush(); parts.push(child) }
      }
      flush()
      return parts
    }
    return 'children' in block && Array.isArray(block.children) && !isRow(block)
      ? [{ ...block, children: splitAtBlocks(block.children) } as Block] : [block]
  })
}

interface Violation { slot: string; block: string }

function slotViolations(slot: string, content: unknown, found: Violation[], lines: boolean): void {
  if (!Array.isArray(content)) return
  for (const item of content as Blocks) {
    if (isInlineContent(item)) continue
    // A field's own lines are paragraphs of inline content.
    if (lines && item.type === 'p' && isRow(item)) { slotViolations(slot, item.children, found, false); continue }
    found.push({ slot, block: item.id })
  }
}

function violations(blocks: Blocks, field?: string, skipFrozen = false): Violation[] {
  const found: Violation[] = []
  const visit = (items: Blocks) => {
    for (const block of items) {
      if (skipFrozen && Object.isFrozen(block)) continue
      if (isRow(block)) slotViolations(block.id, children(block), found, false)
      if (block.type === 'question') {
        slotViolations(`${block.id}.question`, block.question, found, true)
        slotViolations(`${block.id}.explanation`, block.explanation, found, true)
        if ('opts' in block) for (const [index, option] of block.opts.entries()) slotViolations(`${block.id}.opts.${index}`, option, found, true)
      }
      if ('caption' in block) slotViolations(`${block.id}.caption`, block.caption, found, true)
      if (block.type === 'quote') slotViolations(`${block.id}.author`, block.author, found, true)
      if (!isRow(block)) visit(children(block))
    }
  }
  if (field) slotViolations(field, blocks, found, true)
  else visit(blocks)
  return found
}

/**
 * INV-LEVEL: block-level content in inline-only slots, as `slot:block` keys: a text row's (a cell's too) non-inline
 * content, and a question prompt's, option's, explanation's, caption's or quote author's. `field` names the slot `blocks`
 * is when an editing scope is a field.
 */
export function levelViolations(blocks: Blocks, field?: string): Set<string> {
  return new Set(violations(blocks, field).map(({ slot, block }) => `${slot}:${block}`))
}

/**
 * Throws when a mutation left block-level content in an inline-only slot that was not there before it. A block already
 * misplaced (a stored document from before the rule, a merged remote edit) is not the mutation's, wherever the mutation
 * moves it.
 */
export function assertLevels(before: Blocks | undefined, after: Blocks, field?: string): void {
  // A frozen block of `after` is a committed view block the mutation never wrote (apply-intent.ts `draftBlocks`): it is
  // in `before` as it is, so it adds no violation.
  const now = violations(after, field, true)
  if (now.length === 0) return
  const earlier = new Set(before ? violations(before, field).map(({ block }) => block) : [])
  const added = now.filter(({ block }) => !earlier.has(block))
  if (added.length > 0) throw new Error(`INV-LEVEL: block-level content in an inline-only slot (${added.map(({ slot, block }) => `${slot}:${block}`).join(', ')})`)
}
